import { useEffect, useMemo, useState } from 'react';
import { createConsumer } from '@rails/actioncable';
import { onlineManager } from '@tanstack/react-query';
export default function useActionCable(url: string) {
  const actionCable = useMemo(() => createConsumer(`${url}`), [url]);

  const [isNetworkConnected, setIsNetworkConnected] = useState(
    onlineManager.isOnline(),
  );

  useEffect(() => {
    const unsubscribe = onlineManager.subscribe(() => {
      setIsNetworkConnected(onlineManager.isOnline());
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  // Immediate connect/disconnect on network changes
  useEffect(() => {
    if (isNetworkConnected && !actionCable.connection.isOpen()) {
      actionCable.connect();
    } else if (!isNetworkConnected && actionCable.connection.isOpen()) {
      actionCable.disconnect();
    }
  }, [isNetworkConnected, actionCable]);

  // Periodic fallback to reconnect if silently disconnected
  // Mobile OS (iOS/Android) may pause or kill background JavaScript processes.
  useEffect(() => {
    if (!isNetworkConnected) {
      return;
    }

    const interval = setInterval(() => {
      if (!actionCable.connection.isOpen()) {
        actionCable.connect();
      }
    }, 10_000);

    return () => clearInterval(interval);
  }, [isNetworkConnected, actionCable]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      actionCable.disconnect();
    };
  }, [actionCable]);

  return { actionCable };
}
